import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type {
  FaClientSummary,
  MissionTemplateSummary,
} from "@/services/api/agentic-v2";

/**
 * Dialog for creating a new mission.
 *
 * Phase 1: pick a template, pick an fa_client, POST. The resulting
 * mission_id is bubbled up to the parent so it can navigate to the
 * detail view.
 */
export interface MissionCreateDialogProps {
  open: boolean;
  templates: MissionTemplateSummary[];
  onClose: () => void;
  onCreated: (missionId: string) => void;
}

export function MissionCreateDialog({
  open,
  templates,
  onClose,
  onCreated,
}: MissionCreateDialogProps) {
  const [templateId, setTemplateId] = useState<string>("");
  const [faClientId, setFaClientId] = useState<string>("");
  const [faClients, setFaClients] = useState<FaClientSummary[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (templates.length > 0 && !templateId) setTemplateId(templates[0]._id);
  }, [open, templates, templateId]);

  useEffect(() => {
    if (!open) return;
    setLoadingClients(true);
    AgenticV2API.listFaClients(undefined, 200, "all")
      .then((rows) => {
        setFaClients(rows);
        if (rows.length > 0 && !faClientId) setFaClientId(rows[0]._id);
      })
      .catch(() => setFaClients([]))
      .finally(() => setLoadingClients(false));
    // intentionally only re-run when dialog opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSubmit = useMemo(
    () => Boolean(templateId && faClientId) && !submitting,
    [templateId, faClientId, submitting]
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await AgenticV2API.createMission({
        template_id: templateId,
        fa_client_id: faClientId,
      });
      onCreated(result.mission_id);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create mission";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>New Mission</DialogTitle>
      <DialogDescription>
        Start a mission from a template for an existing client.
      </DialogDescription>
      <DialogBody>
        <div className="space-y-4">
          <Field label="Mission template">
            <Select
              value={templateId}
              onChange={setTemplateId}
              options={templates.map((t) => ({ value: t._id, label: t.name }))}
              placeholder={
                templates.length === 0
                  ? "No templates available"
                  : "Select a template"
              }
              disabled={templates.length === 0}
              testId="mission-create-template"
            />
          </Field>

          <Field label="Client">
            <Select
              value={faClientId}
              onChange={setFaClientId}
              options={faClients.map((c) => ({
                value: c._id,
                label: c.display_name,
              }))}
              placeholder={
                loadingClients
                  ? "Loading clients..."
                  : faClients.length === 0
                    ? "No clients available"
                    : "Select a client"
              }
              disabled={loadingClients || faClients.length === 0}
              testId="mission-create-fa-client"
            />
          </Field>

          {error && (
            <p className="text-sm text-red-400" role="alert">
              {error}
            </p>
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="indigo"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="mission-create-submit"
        >
          {submitting ? "Creating..." : "Create mission"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-slate-400">{label}</span>
      {children}
    </label>
  );
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  testId?: string;
}

function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  testId,
}: SelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testId}
      className="bg-slate-900/60 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-slate-600 disabled:opacity-50"
    >
      {!value && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
